// Page Mes Contacts - Harmonisée avec le design "Mon Espace"
// DESIGN UNIQUEMENT - Ne pas modifier les fonctionnalités

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Search, Star, Clock, UserPlus, Mail, Phone,
  ChevronRight, MoreHorizontal, X, Trash2, Edit3, MessageCircle,
  MapPin, Building2, Calendar, Heart, Send, Sparkles, StickyNote,
  FileText, PhoneCall, ExternalLink, ArrowLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

// Icônes et couleurs pour la timeline
const TIMELINE_ICONS = {
  message: MessageCircle,
  email: Mail,
  note: StickyNote,
  call: PhoneCall,
  document: FileText,
};

const TIMELINE_COLORS = {
  message: 'bg-purple-100 text-purple-600 border-purple-200',
  email: 'bg-blue-100 text-blue-600 border-blue-200',
  note: 'bg-amber-100 text-amber-600 border-amber-200',
  call: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  document: 'bg-green-100 text-green-600 border-green-200',
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fonction pour obtenir le token
const getToken = () => {
  return localStorage.getItem('buyer_token') || 
         localStorage.getItem('client_token') || 
         localStorage.getItem('authToken');
};

export default function MesContactsPage({ onAskAbra, onBackToHub }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactCounts, setContactCounts] = useState({});
  const isDarkMode = false;
  
  // États pour la fiche contact détaillée
  const [detailTab, setDetailTab] = useState('historique');
  const [contactHistory, setContactHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [gmailConnected, setGmailConnected] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);
  
  // Détection mobile pour adapter le layout
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Effet pour cacher le bouton flottant "Demander à Abra" et la navbar quand la modale est ouverte
  useEffect(() => {
    if (showCreateModal) {
      document.body.classList.add('modal-contact-open');
      document.body.classList.add('modal-fullscreen-open');
    } else {
      document.body.classList.remove('modal-contact-open');
      document.body.classList.remove('modal-fullscreen-open');
    }
    return () => {
      document.body.classList.remove('modal-contact-open');
      document.body.classList.remove('modal-fullscreen-open');
    };
  }, [showCreateModal]);
  
  // Refs et états pour le scroll des filtres
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const filters = ['Tous', 'Récents', 'Favoris'];
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Nouveau contact formulaire - Structure améliorée
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    contactType: 'Personnel',
    phonePrefix: '+33',
    phoneNumber: '',
    email: '',
    company: '',
    notes: '',
    // Champs avancés
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    birthday: '',
    website: '',
    // Réseaux sociaux séparés
    facebook: '',
    instagram: '',
    tiktok: '',
    twitter: '',
    linkedin: '',
    jobTitle: '',
    source: 'Manuel',
    // Invitation messagerie privée
    inviteToChat: false
  });
  
  // État pour afficher/masquer les champs avancés
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  // Types de contact disponibles
  const contactTypes = ['Personnel', 'Famille', 'Ami', 'Pro', 'Prestataire'];
  
  // Sources de contact
  const contactSources = ['Manuel', 'Marketplace', 'Réseau social', 'Prestataire', 'Groupe privé', 'Autre'];
  
  // Préfixes téléphoniques par pays
  const phoneCountries = [
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+32', country: 'Belgique', flag: '🇧🇪' },
    { code: '+41', country: 'Suisse', flag: '🇨🇭' },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
    { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
    { code: '+34', country: 'Espagne', flag: '🇪🇸' },
    { code: '+39', country: 'Italie', flag: '🇮🇹' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+212', country: 'Maroc', flag: '🇲🇦' },
    { code: '+213', country: 'Algérie', flag: '🇩🇿' },
    { code: '+216', country: 'Tunisie', flag: '🇹🇳' },
    { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
  ];
  
  // Reset du formulaire
  const resetNewContactForm = () => {
    setNewContact({
      firstName: '',
      lastName: '',
      contactType: 'Personnel',
      phonePrefix: '+33',
      phoneNumber: '',
      email: '',
      company: '',
      notes: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'France',
      birthday: '',
      website: '',
      facebook: '',
      instagram: '',
      tiktok: '',
      twitter: '',
      linkedin: '',
      jobTitle: '',
      source: 'Manuel',
      inviteToChat: false
    });
    setShowAdvancedFields(false);
  };

  // Charger les contacts
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        // Contacts de démonstration si pas connecté
        setContacts([
          { id: 1, name: 'Marie Dupont', email: 'marie.dupont@email.com', phone: '06 12 34 56 78', company: 'Abracada', isFavorite: true, lastContact: '2024-01-15', category: 'Travail' },
          { id: 2, name: 'Pierre Martin', email: 'pierre.m@email.com', phone: '06 98 76 54 32', company: '', isFavorite: false, lastContact: '2024-01-10', category: 'Ami' },
          { id: 3, name: 'Sophie Bernard', email: 'sophie.b@email.com', phone: '07 11 22 33 44', company: 'Freelance', isFavorite: true, lastContact: '2024-01-14', category: 'Travail' },
        ]);
        setContactCounts({ 'Tous': 3, 'Récents': 3, 'Favoris': 2, 'Invitations': 0 });
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        // Calculer les compteurs
        const counts = {
          'Tous': (data.contacts || []).length,
          'Récents': (data.contacts || []).filter(c => {
            const lastContact = new Date(c.lastContact);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return lastContact >= weekAgo;
          }).length,
          'Favoris': (data.contacts || []).filter(c => c.isFavorite).length,
          'Invitations': (data.invitations || []).length
        };
        setContactCounts(counts);
      }
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
    setLoading(false);
  };

  // Filtrer les contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    switch (activeFilter) {
      case 'Favoris':
        return contact.isFavorite;
      case 'Récents':
        const lastContact = new Date(contact.lastContact);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return lastContact >= weekAgo;
      case 'Invitations':
        return contact.status === 'pending';
      default:
        return true;
    }
  });

  // Toggle favori
  const toggleFavorite = async (contactId) => {
    setContacts(contacts.map(c => 
      c.id === contactId ? { ...c, isFavorite: !c.isFavorite } : c
    ));
    toast.success('Contact mis à jour');
  };

  // Créer un contact
  const handleCreateContact = async () => {
    if (!newContact.firstName) {
      toast.error('Le prénom est requis');
      return;
    }
    
    // Construire le nom complet
    const fullName = newContact.lastName 
      ? `${newContact.firstName} ${newContact.lastName}`.trim()
      : newContact.firstName;
    
    // Construire le numéro de téléphone complet avec préfixe
    const fullPhone = newContact.phoneNumber 
      ? `${newContact.phonePrefix} ${newContact.phoneNumber}`.trim()
      : '';
    
    // Construire l'objet réseaux sociaux
    const socialNetworks = {
      facebook: newContact.facebook,
      instagram: newContact.instagram,
      tiktok: newContact.tiktok,
      twitter: newContact.twitter,
      linkedin: newContact.linkedin
    };
    
    // Simuler la création
    const contact = {
      id: Date.now(),
      name: fullName,
      firstName: newContact.firstName,
      lastName: newContact.lastName,
      email: newContact.email,
      phone: fullPhone,
      phonePrefix: newContact.phonePrefix,
      phoneNumber: newContact.phoneNumber,
      company: newContact.company,
      notes: newContact.notes,
      contactType: newContact.contactType,
      address: newContact.address,
      city: newContact.city,
      postalCode: newContact.postalCode,
      country: newContact.country,
      birthday: newContact.birthday,
      website: newContact.website,
      socialNetworks: socialNetworks,
      jobTitle: newContact.jobTitle,
      source: newContact.source,
      isFavorite: false,
      lastContact: new Date().toISOString(),
      category: newContact.contactType,
      // Statut d'invitation messagerie
      chatInviteSent: newContact.inviteToChat,
      chatInviteStatus: newContact.inviteToChat ? 'pending' : null
    };
    
    setContacts([contact, ...contacts]);
    setShowCreateModal(false);
    resetNewContactForm();
    
    // Message de confirmation adapté
    if (newContact.inviteToChat) {
      toast.success(`Contact ajouté ! Invitation envoyée à ${fullName} pour la messagerie privée.`);
    } else {
      toast.success('Contact ajouté');
    }
  };

  // Supprimer un contact
  const handleDeleteContact = async (contactId) => {
    setContacts(contacts.filter(c => c.id !== contactId));
    setSelectedContact(null);
    toast.success('Contact supprimé');
  };

  // Charger l'historique d'un contact via l'API
  const loadContactHistory = async (contact) => {
    setLoadingHistory(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setContactHistory(data.history || []);
        setLoadingHistory(false);
        return;
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
    
    // Fallback vers les données de démonstration si l'API échoue
    const demoHistory = [
      {
        id: 'h1',
        type: 'message',
        title: 'Message envoyé',
        content: 'Salut ! On se voit toujours samedi ?',
        direction: 'outgoing',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'h2',
        type: 'message',
        title: 'Message reçu',
        content: 'Oui parfait, à 14h au café habituel !',
        direction: 'incoming',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 3600000).toISOString(),
      },
      {
        id: 'h3',
        type: 'email',
        title: 'Email envoyé',
        content: 'Objet : Documents pour le projet',
        direction: 'outgoing',
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'h4',
        type: 'call',
        title: 'Appel sortant',
        content: 'Durée : 5 min 32 sec',
        direction: 'outgoing',
        date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'h5',
        type: 'note',
        title: 'Note personnelle',
        content: 'Anniversaire le 15 mars - penser à lui souhaiter !',
        direction: 'note',
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
    
    setContactHistory(demoHistory);
    setLoadingHistory(false);
  };
  
  // Ajouter une note à un contact via l'API
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('La note ne peut pas être vide');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contacts/${selectedContact.id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'note',
          title: 'Note personnelle',
          content: newNote,
          direction: 'note'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setContactHistory([data.note, ...contactHistory]);
        setNewNote('');
        setShowAddNote(false);
        toast.success('Note ajoutée');
        return;
      }
    } catch (error) {
      console.error('Erreur ajout note:', error);
    }
    
    // Fallback local si l'API échoue
    const note = {
      id: `note_${Date.now()}`,
      type: 'note',
      title: 'Note personnelle',
      content: newNote,
      direction: 'note',
      date: new Date().toISOString(),
    };
    
    setContactHistory([note, ...contactHistory]);
    setNewNote('');
    setShowAddNote(false);
    toast.success('Note ajoutée');
  };
  
  // Quand on sélectionne un contact, charger son historique
  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setDetailTab('infos'); // Par défaut sur Informations
    loadContactHistory(contact);
    
    // Vérifier le statut Gmail
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contacts/gmail/status`);
      if (response.ok) {
        const data = await response.json();
        setGmailConnected(data.connected);
      }
    } catch (error) {
      console.error('Erreur vérification Gmail:', error);
    }
  };
  
  // Synchroniser les emails avec Gmail
  const syncContactEmails = async () => {
    if (!selectedContact?.email) {
      toast.error('Ce contact n\'a pas d\'adresse email');
      return;
    }
    
    setSyncingEmails(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contacts/${selectedContact.id}/sync-emails`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.synced_count} emails synchronisés`);
        // Recharger l'historique
        loadContactHistory(selectedContact);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de synchronisation');
      }
    } catch (error) {
      console.error('Erreur sync emails:', error);
      toast.error('Erreur de synchronisation Gmail');
    } finally {
      setSyncingEmails(false);
    }
  };
  
  // Formater la date
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };
  
  // Formater la date courte
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const totalContacts = contactCounts['Tous'] || contacts.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mes Notes */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
              Mes contacts
            </h1>
            {/* Sous-texte masqué sur mobile */}
            {!isMobile && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Gère et organise tes contacts personnels et professionnels
              </p>
            )}
          </div>
          {isMobile ? (
            <button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-contact-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              Nouveau contact
            </button>
          ) : (
            <Button 
              onClick={() => setShowCreateModal(true)}
              data-testid="create-contact-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
            >
              <Plus size={16} />
              Nouveau contact
            </Button>
          )}
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Users size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalContacts} contact{totalContacts > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un contact..."
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Filtres - Style identique à Mes Notes / Mes Messages */}
      <div className="flex-shrink-0 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filters.map((filter) => {
              const count = contactCounts[filter] || 0;
              
              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  data-testid={`filter-${filter.toLowerCase()}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {filter}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                        activeFilter === filter 
                          ? 'bg-white/25 text-white' 
                          : 'bg-orange-500 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Flèche orange discrète */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Liste des contacts */}
      <div className={`flex-1 rounded-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredContacts.length === 0 ? (
          /* État vide - Style harmonisé */
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 max-w-sm">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
              }`}>
              <Users size={28} className="text-orange-500" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Aucun contact
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {activeFilter === 'Favoris' 
                ? 'Marque tes contacts préférés en favoris pour les retrouver facilement.'
                : activeFilter === 'Invitations'
                ? 'Tu n\'as pas d\'invitations en attente.'
                : 'Ajoute ton premier contact pour commencer à organiser ton carnet d\'adresses.'}
            </p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
            >
              <UserPlus size={16} />
              Ajouter un contact
            </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto h-full">
            {filteredContacts.map((contact) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-4 cursor-pointer transition-colors ${
                  isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                }`}
                onClick={() => handleSelectContact(contact)}
                data-testid={`contact-${contact.id}`}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {contact.name?.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {contact.name}
                      </h3>
                      {contact.isFavorite && (
                        <Star size={14} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                      )}
                    </div>
                    {contact.company && (
                      <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {contact.company}
                      </p>
                    )}
                    {/* Mobile: téléphone au-dessus du mail, sur des lignes séparées */}
                    {isMobile ? (
                      <div className="flex flex-col gap-0.5 mt-1">
                        {contact.phone && (
                          <span className={`text-xs flex items-center gap-1 whitespace-nowrap ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Phone size={10} className="flex-shrink-0" />
                            {contact.phone}
                          </span>
                        )}
                        {contact.email && (
                          <span className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Mail size={10} className="flex-shrink-0" />
                            <span className="truncate">{contact.email}</span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mt-1">
                        {contact.email && (
                          <span className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Mail size={10} />
                            <span className="truncate max-w-[120px]">{contact.email}</span>
                          </span>
                        )}
                        {contact.phone && (
                          <span className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Phone size={10} />
                            {contact.phone}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(contact.id); }}
                      className={`p-2 rounded-lg transition-colors ${
                        contact.isFavorite 
                          ? 'text-amber-400' 
                          : isDarkMode ? 'text-gray-500 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      <Star size={16} className={contact.isFavorite ? 'fill-amber-400' : ''} />
                    </button>
                    <ChevronRight size={16} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Nouveau Contact - Version mobile fullscreen via Portal */}
      {showCreateModal && isMobile && createPortal(
        <AnimatePresence>
          <motion.div
            key="new-contact-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed inset-0 z-[10002] flex flex-col ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
          >
            {/* Header avec safe area */}
            <header 
              className={`flex items-center gap-2 px-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => { setShowCreateModal(false); resetNewContactForm(); }}
                className={`p-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className={`text-[15px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nouveau contact</h1>
            </header>

            {/* Formulaire scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              
              {/* Prénom et Nom */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Prénom <span className="text-orange-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newContact.firstName}
                    onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                    placeholder="Prénom"
                    className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Nom
                  </label>
                  <input
                    type="text"
                    value={newContact.lastName}
                    onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                    placeholder="Nom"
                    className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>

              {/* Type de contact */}
              <div>
                <label className={`block text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Type de contact
                </label>
                <div className="flex flex-wrap gap-2">
                  {contactTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setNewContact({...newContact, contactType: type})}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        newContact.contactType === type
                          ? 'bg-orange-500 text-white'
                          : isDarkMode 
                            ? 'bg-slate-800 text-gray-300 border border-slate-700' 
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Email
                </label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                  placeholder="email@exemple.com"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                  placeholder="+33 6 00 00 00 00"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Entreprise */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Entreprise
                </label>
                <input
                  type="text"
                  value={newContact.company}
                  onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                  placeholder="Nom de l'entreprise"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Adresse */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Adresse
                </label>
                <input
                  type="text"
                  value={newContact.address}
                  onChange={(e) => setNewContact({...newContact, address: e.target.value})}
                  placeholder="Adresse complète"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Notes */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                  Notes
                </label>
                <textarea
                  value={newContact.notes}
                  onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                  placeholder="Ajouter des notes..."
                  rows={3}
                  className={`w-full px-3 py-2.5 rounded-lg text-sm border transition-colors focus:ring-0 focus:border-orange-500 resize-none ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            {/* Footer fixe */}
            <div className={`p-4 border-t ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-gray-50/50'}`}>
              {onAskAbra && (
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    onAskAbra({ contact: newContact, isNew: true });
                  }}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all ${
                    isDarkMode 
                      ? 'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30' 
                      : 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                  }`}
                >
                  <Sparkles size={16} />
                  Remplir avec Abracada
                </button>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowCreateModal(false); resetNewContactForm(); }}
                  className={`flex-1 h-11 rounded-xl font-semibold ${
                    isDarkMode ? 'border-slate-600 text-gray-300' : 'border-gray-300 text-[#1e3a5f]'
                  }`}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCreateContact}
                  className="flex-1 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold"
                >
                  Ajouter
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Modal Nouveau Contact - Version desktop */}
      <AnimatePresence>
        {showCreateModal && !isMobile && (
          <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => { setShowCreateModal(false); resetNewContactForm(); }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full max-w-[95vw] sm:max-w-lg lg:max-w-xl rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              }`}
              style={{ maxHeight: '85vh' }}
            >
              {/* Header modal */}
              <div className={`flex items-start justify-between p-5 pb-4 border-b ${
                isDarkMode ? 'border-slate-700' : 'border-gray-100'
              }`}>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                    Nouveau contact
                  </h2>
                  <p className={`hidden sm:block text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Ajoute une personne à tes contacts et retrouve-la facilement dans tes messages, groupes et demandes.
                  </p>
                </div>
                <button
                  onClick={() => { setShowCreateModal(false); resetNewContactForm(); }}
                  className={`p-2 rounded-xl transition-colors flex-shrink-0 ml-4 ${
                    isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Formulaire scrollable */}
              <div className="p-5 space-y-5 overflow-y-auto flex-1" style={{ maxHeight: 'calc(85vh - 220px)' }}>
                
                {/* Prénom et Nom */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                      Prénom <span className="text-orange-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newContact.firstName}
                      onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                      placeholder="Prénom"
                      className={`w-full px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                      Nom
                    </label>
                    <input
                      type="text"
                      value={newContact.lastName}
                      onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                      placeholder="Nom"
                      className={`w-full px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                {/* Type de contact - Scrollable sur mobile */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Type de contact
                  </label>
                  <div className="relative flex items-center">
                    <div 
                      className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 pr-6 pb-1"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {contactTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setNewContact({...newContact, contactType: type})}
                          className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                            newContact.contactType === type
                              ? 'bg-orange-500 text-white shadow-md'
                              : isDarkMode
                                ? 'bg-slate-800 text-gray-300 border border-slate-700 hover:border-orange-500/50'
                                : 'bg-white text-[#1e3a5f] border-2 border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                    {/* Flèche orange pour indiquer le scroll */}
                    <div className="flex-shrink-0 flex items-center pointer-events-none sm:hidden">
                      <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                {/* Téléphone avec préfixe pays et Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                      Téléphone
                    </label>
                    <div className="flex gap-2">
                      {/* Sélecteur de pays */}
                      <select
                        value={newContact.phonePrefix}
                        onChange={(e) => setNewContact({...newContact, phonePrefix: e.target.value})}
                        className={`flex-shrink-0 w-[100px] px-2 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 appearance-none cursor-pointer ${
                          isDarkMode 
                            ? 'bg-slate-800 border-slate-700 text-white' 
                            : 'bg-gray-50 border-gray-200 text-gray-900 hover:border-gray-300'
                        }`}
                        style={{ backgroundImage: 'none' }}
                      >
                        {phoneCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      {/* Numéro de téléphone */}
                      <input
                        type="tel"
                        value={newContact.phoneNumber}
                        onChange={(e) => setNewContact({...newContact, phoneNumber: e.target.value})}
                        placeholder="6 12 34 56 78"
                        className={`flex-1 px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 ${
                          isDarkMode 
                            ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      placeholder="email@exemple.com"
                      className={`w-full px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                {/* Option : Inviter sur la messagerie privée */}
                <div className={`p-4 rounded-xl border-2 transition-all ${
                  newContact.inviteToChat 
                    ? (isDarkMode ? 'border-orange-500/50 bg-orange-500/10' : 'border-orange-300 bg-orange-50/50')
                    : (isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50/50')
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="pt-0.5">
                      <input
                        type="checkbox"
                        checked={newContact.inviteToChat}
                        onChange={(e) => setNewContact({...newContact, inviteToChat: e.target.checked})}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        newContact.inviteToChat
                          ? 'bg-orange-500 border-orange-500'
                          : (isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300 bg-white')
                      }`}>
                        {newContact.inviteToChat && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold text-sm ${
                        newContact.inviteToChat 
                          ? 'text-orange-600' 
                          : (isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]')
                      }`}>
                        <MessageCircle size={14} className="inline mr-1.5 -mt-0.5" />
                        Inviter sur la messagerie privée
                      </div>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        Cette personne recevra une notification pour discuter avec toi sur Abracada Chat
                      </p>
                    </div>
                  </label>
                </div>

                {/* Entreprise */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Entreprise
                  </label>
                  <input
                    type="text"
                    value={newContact.company}
                    onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                    placeholder="Nom de l'entreprise"
                    className={`w-full px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                    }`}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Notes
                  </label>
                  <textarea
                    value={newContact.notes}
                    onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                    placeholder="Ajouter des notes..."
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl text-sm border-2 transition-colors focus:ring-0 focus:border-orange-500 resize-none ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
                    }`}
                  />
                </div>

                {/* Lien pour afficher plus d'infos */}
                <button
                  type="button"
                  onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    isDarkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'
                  }`}
                >
                  <Plus size={16} className={`transition-transform ${showAdvancedFields ? 'rotate-45' : ''}`} />
                  {showAdvancedFields ? 'Masquer les infos supplémentaires' : 'Ajouter plus d\'infos'}
                </button>

                {/* Champs avancés */}
                <AnimatePresence>
                  {showAdvancedFields && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      <div className={`p-4 rounded-xl space-y-4 ${
                        isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'
                      }`}>
                        {/* Adresse */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Adresse
                          </label>
                          <input
                            type="text"
                            value={newContact.address}
                            onChange={(e) => setNewContact({...newContact, address: e.target.value})}
                            placeholder="Adresse complète"
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>

                        {/* Ville, Code postal */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Ville
                            </label>
                            <input
                              type="text"
                              value={newContact.city}
                              onChange={(e) => setNewContact({...newContact, city: e.target.value})}
                              placeholder="Ville"
                              className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                isDarkMode 
                                  ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                              }`}
                            />
                          </div>
                          <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Code postal
                            </label>
                            <input
                              type="text"
                              value={newContact.postalCode}
                              onChange={(e) => setNewContact({...newContact, postalCode: e.target.value})}
                              placeholder="75000"
                              className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                isDarkMode 
                                  ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                              }`}
                            />
                          </div>
                        </div>

                        {/* Pays */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Pays
                          </label>
                          <input
                            type="text"
                            value={newContact.country}
                            onChange={(e) => setNewContact({...newContact, country: e.target.value})}
                            placeholder="France"
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>

                        {/* Date d'anniversaire */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Date d'anniversaire
                          </label>
                          <input
                            type="date"
                            value={newContact.birthday}
                            onChange={(e) => setNewContact({...newContact, birthday: e.target.value})}
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white' 
                                : 'bg-white border-gray-200 text-gray-900'
                            }`}
                          />
                        </div>

                        {/* Site internet */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Site internet
                          </label>
                          <input
                            type="url"
                            value={newContact.website}
                            onChange={(e) => setNewContact({...newContact, website: e.target.value})}
                            placeholder="https://..."
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>

                        {/* Réseaux sociaux - Champs séparés avec logos */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-3 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Réseaux sociaux
                          </label>
                          <div className="space-y-3">
                            {/* Facebook */}
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-slate-600' : 'bg-[#1877F2]/10'
                              }`}>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={newContact.facebook}
                                onChange={(e) => setNewContact({...newContact, facebook: e.target.value})}
                                placeholder="Pseudo ou lien Facebook"
                                className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                  isDarkMode 
                                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                              />
                            </div>
                            
                            {/* Instagram */}
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-slate-600' : 'bg-gradient-to-br from-[#833AB4]/10 via-[#FD1D1D]/10 to-[#F77737]/10'
                              }`}>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
                                  <defs>
                                    <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                                      <stop offset="0%" stopColor="#FFDC80"/>
                                      <stop offset="25%" stopColor="#F77737"/>
                                      <stop offset="50%" stopColor="#FD1D1D"/>
                                      <stop offset="75%" stopColor="#833AB4"/>
                                      <stop offset="100%" stopColor="#5851DB"/>
                                    </linearGradient>
                                  </defs>
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={newContact.instagram}
                                onChange={(e) => setNewContact({...newContact, instagram: e.target.value})}
                                placeholder="Pseudo ou lien Instagram"
                                className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                  isDarkMode 
                                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                              />
                            </div>
                            
                            {/* TikTok */}
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-slate-600' : 'bg-black/5'
                              }`}>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#000000">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={newContact.tiktok}
                                onChange={(e) => setNewContact({...newContact, tiktok: e.target.value})}
                                placeholder="Pseudo ou lien TikTok"
                                className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                  isDarkMode 
                                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                              />
                            </div>
                            
                            {/* X (Twitter) */}
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-slate-600' : 'bg-black/5'
                              }`}>
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#000000">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={newContact.twitter}
                                onChange={(e) => setNewContact({...newContact, twitter: e.target.value})}
                                placeholder="Pseudo ou lien X"
                                className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                  isDarkMode 
                                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                              />
                            </div>
                            
                            {/* LinkedIn */}
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-slate-600' : 'bg-[#0A66C2]/10'
                              }`}>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={newContact.linkedin}
                                onChange={(e) => setNewContact({...newContact, linkedin: e.target.value})}
                                placeholder="Pseudo ou lien LinkedIn"
                                className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                                  isDarkMode 
                                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                }`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Métier / fonction */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Métier / Fonction
                          </label>
                          <input
                            type="text"
                            value={newContact.jobTitle}
                            onChange={(e) => setNewContact({...newContact, jobTitle: e.target.value})}
                            placeholder="Développeur, Artisan, Manager..."
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>

                        {/* Source du contact */}
                        <div>
                          <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Source du contact
                          </label>
                          <select
                            value={newContact.source}
                            onChange={(e) => setNewContact({...newContact, source: e.target.value})}
                            className={`w-full px-4 py-2.5 rounded-xl text-sm border transition-colors focus:ring-0 focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-700 border-slate-600 text-white' 
                                : 'bg-white border-gray-200 text-gray-900'
                            }`}
                          >
                            {contactSources.map(source => (
                              <option key={source} value={source}>{source}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer modal */}
              <div className={`p-5 pt-4 border-t ${
                isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-gray-50/50'
              }`}>
                {/* Bouton Remplir avec Abracada */}
                {onAskAbra && (
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      onAskAbra({ contact: newContact, isNew: true });
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all ${
                      isDarkMode 
                        ? 'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30' 
                        : 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                    }`}
                  >
                    <Sparkles size={16} />
                    Remplir avec Abracada
                  </button>
                )}
                
                {/* Boutons Annuler et Ajouter */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCreateModal(false); resetNewContactForm(); }}
                    className={`flex-1 h-12 rounded-xl font-semibold transition-all ${
                      isDarkMode 
                        ? 'border-slate-600 text-gray-300 hover:bg-slate-800' 
                        : 'border-gray-300 text-[#1e3a5f] hover:bg-gray-100'
                    }`}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleCreateContact}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all"
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Détail Contact - Style identique à Nouveau Contact */}
      <AnimatePresence>
        {selectedContact && (
          <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setSelectedContact(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full max-w-[95vw] sm:max-w-lg lg:max-w-xl rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              }`}
              style={{ maxHeight: '85vh' }}
            >
              {/* Header fixe - Style identique à Nouveau Contact */}
              <div className={`flex items-start justify-between p-5 pb-4 border-b ${
                isDarkMode ? 'border-slate-700' : 'border-gray-100'
              }`}>
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {selectedContact.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                        {selectedContact.name}
                      </h2>
                      {selectedContact.isFavorite && (
                        <Star size={16} className="text-amber-400 fill-amber-400" />
                      )}
                    </div>
                    {selectedContact.company && (
                      <p className={`hidden sm:block text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedContact.company}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedContact(null)}
                  className={`p-2 rounded-xl transition-colors flex-shrink-0 ml-4 ${
                    isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Contenu scrollable */}
              <div className="p-5 space-y-5 overflow-y-auto flex-1" style={{ maxHeight: 'calc(85vh - 220px)' }}>
                
                {/* Infos contact cliquables */}
                <div className="flex flex-wrap gap-2">
                  {selectedContact.phone && (
                    <a 
                      href={`tel:${selectedContact.phone}`}
                      className="flex items-center gap-2 px-4 py-2.5 bg-cyan-50 hover:bg-cyan-100 rounded-xl text-cyan-700 text-sm font-medium transition-colors border border-cyan-100"
                    >
                      <Phone size={16} />
                      <span className="whitespace-nowrap">{selectedContact.phone}</span>
                    </a>
                  )}
                  {selectedContact.email && (
                    <a 
                      href={`mailto:${selectedContact.email}`}
                      className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl text-blue-700 text-sm font-medium transition-colors border border-blue-100"
                    >
                      <Mail size={16} className="flex-shrink-0" />
                      <span className="truncate max-w-[180px]">{selectedContact.email}</span>
                    </a>
                  )}
                </div>

                {/* Actions rapides - Style filtres avec scroll horizontal */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-[#1e3a5f]'}`}>
                    Actions rapides
                  </label>
                  <div className="relative flex items-center">
                    <div 
                      className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                      {/* SMS - Ouvre l'app SMS native */}
                      <a 
                        href={selectedContact.phone ? `sms:${selectedContact.phone.replace(/\s/g, '')}` : '#'}
                        onClick={(e) => {
                          if (!selectedContact.phone) {
                            e.preventDefault();
                            toast.error('Ce contact n\'a pas de numéro de téléphone');
                          }
                        }}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-green-500 text-white shadow-sm"
                      >
                        <MessageCircle size={14} />
                        SMS
                      </a>
                      {/* Chat - Messagerie interne Abracada */}
                      <button 
                        onClick={() => {
                          toast.success(`Ouverture du chat avec ${selectedContact.name}`);
                          // TODO: Ouvrir la messagerie Abracadatchat avec ce contact
                        }}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-orange-500 text-white shadow-sm"
                      >
                        <Send size={14} />
                        Chat
                      </button>
                      {/* Email */}
                      <a 
                        href={selectedContact.email ? `mailto:${selectedContact.email}` : '#'}
                        onClick={(e) => {
                          if (!selectedContact.email) {
                            e.preventDefault();
                            toast.error('Ce contact n\'a pas d\'adresse email');
                          }
                        }}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      >
                        <Mail size={14} />
                        Email
                      </a>
                      {/* Appeler */}
                      <a 
                        href={selectedContact.phone ? `tel:${selectedContact.phone.replace(/\s/g, '')}` : '#'}
                        onClick={(e) => {
                          if (!selectedContact.phone) {
                            e.preventDefault();
                            toast.error('Ce contact n\'a pas de numéro de téléphone');
                          }
                        }}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      >
                        <Phone size={14} />
                        Appeler
                      </a>
                      {/* Note */}
                      <button 
                        onClick={() => setShowAddNote(true)}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      >
                        <StickyNote size={14} />
                        Note
                      </button>
                      {/* Sync Gmail */}
                      {selectedContact.email && (
                        <button 
                          onClick={syncContactEmails}
                          disabled={syncingEmails}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 disabled:opacity-50"
                        >
                          <RefreshCw size={14} className={syncingEmails ? 'animate-spin' : ''} />
                          {syncingEmails ? 'Sync...' : 'Sync Gmail'}
                        </button>
                      )}
                    </div>
                    {/* Indicateur de scroll - flèche orange */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-full bg-gradient-to-l from-white via-white to-transparent flex items-center justify-end pointer-events-none">
                      <ChevronRight size={16} className="text-orange-500" />
                    </div>
                  </div>
                </div>

                {/* Onglets - Informations à gauche, Historique à droite */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setDetailTab('infos')}
                    className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                      detailTab === 'infos'
                        ? 'bg-[#1E3A5F] text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    Informations
                  </button>
                  <button
                    onClick={() => setDetailTab('historique')}
                    className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                      detailTab === 'historique'
                        ? 'bg-[#1E3A5F] text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    Historique
                  </button>
                </div>

                {/* Contenu de l'onglet Informations */}
                {detailTab === 'infos' && (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {selectedContact.phone && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <Phone size={18} className="text-cyan-500" />
                          <div>
                            <p className="text-xs text-gray-500">Téléphone</p>
                            <p className="text-sm font-medium text-gray-800">{selectedContact.phone}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedContact.email && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <Mail size={18} className="text-blue-500" />
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-medium text-gray-800">{selectedContact.email}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedContact.company && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <Building2 size={18} className="text-purple-500" />
                          <div>
                            <p className="text-xs text-gray-500">Entreprise</p>
                            <p className="text-sm font-medium text-gray-800">{selectedContact.company}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedContact.category && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <Users size={18} className="text-orange-500" />
                          <div>
                            <p className="text-xs text-gray-500">Catégorie</p>
                            <p className="text-sm font-medium text-gray-800">{selectedContact.category}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedContact.notes && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <div className="flex items-center gap-2 mb-2">
                            <StickyNote size={16} className="text-amber-500" />
                            <p className="text-xs font-medium text-amber-700">Notes</p>
                          </div>
                          <p className="text-sm text-gray-700">{selectedContact.notes}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Statistiques */}
                    <div className="mt-4">
                      <h4 className="font-semibold text-gray-800 text-sm mb-3">Statistiques</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-purple-50 rounded-xl p-3 text-center">
                          <p className="text-xl font-bold text-purple-600">
                            {contactHistory.filter(h => h.type === 'message').length}
                          </p>
                          <p className="text-xs text-purple-500">Messages</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-3 text-center">
                          <p className="text-xl font-bold text-blue-600">
                            {contactHistory.filter(h => h.type === 'email').length}
                          </p>
                          <p className="text-xs text-blue-500">Emails</p>
                        </div>
                        <div className="bg-cyan-50 rounded-xl p-3 text-center">
                          <p className="text-xl font-bold text-cyan-600">
                            {contactHistory.filter(h => h.type === 'call').length}
                          </p>
                          <p className="text-xs text-cyan-500">Appels</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contenu de l'onglet Historique */}
                {detailTab === 'historique' && (
                  <div className="space-y-4">
                    {loadingHistory ? (
                      <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : contactHistory.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Clock size={24} className="text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">Aucun échange pour le moment</p>
                        <button 
                          onClick={() => setShowAddNote(true)}
                          className="mt-3 text-orange-500 text-sm font-medium hover:underline"
                        >
                          Ajouter une note
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Ligne verticale de timeline */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                        {contactHistory.map((event, index) => {
                          const Icon = TIMELINE_ICONS[event.type] || Clock;
                          const colorClass = TIMELINE_COLORS[event.type] || 'bg-gray-100 text-gray-600 border-gray-200';

                          return (
                            <motion.div
                              key={event.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="relative flex gap-3 pb-4"
                            >
                              {/* Icône */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white z-10 flex-shrink-0 ${colorClass}`}>
                                <Icon size={14} />
                              </div>

                              {/* Contenu */}
                              <div className={`flex-1 rounded-xl p-3 ${
                                event.direction === 'incoming' 
                                  ? 'bg-blue-50 border border-blue-100' 
                                  : event.direction === 'note'
                                  ? 'bg-amber-50 border border-amber-100'
                                  : 'bg-white border border-gray-100'
                              }`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-800 text-sm">{event.title}</p>
                                    <p className="text-gray-600 text-sm mt-1">{event.content}</p>
                                  </div>
                                  <span className={`text-xs whitespace-nowrap flex-shrink-0 ${
                                    event.direction === 'incoming' ? 'text-blue-500' : 
                                    event.direction === 'note' ? 'text-amber-600' : 'text-gray-400'
                                  }`}>
                                    {event.direction === 'incoming' ? '← Reçu' : 
                                     event.direction === 'outgoing' ? '→ Envoyé' : ''}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">{formatDateTime(event.date)}</p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer fixe - Style identique à Nouveau Contact */}
              <div className={`p-5 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                {/* Bouton Remplir avec Abracada */}
                {onAskAbra && (
                  <button
                    onClick={() => {
                      onAskAbra({ contact: selectedContact, isEdit: true });
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all ${
                      isDarkMode 
                        ? 'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30' 
                        : 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                    }`}
                  >
                    <Sparkles size={16} />
                    Remplir avec Abracada
                  </button>
                )}
                
                {/* Boutons Favori, Supprimer, Fermer */}
                <div className="flex gap-3">
                  <button
                    onClick={() => toggleFavorite(selectedContact.id)}
                    className={`flex-1 h-12 flex items-center justify-center gap-2 rounded-xl font-semibold transition-all border ${
                      selectedContact.isFavorite 
                        ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100'
                        : isDarkMode 
                          ? 'border-slate-600 text-gray-300 hover:bg-slate-800' 
                          : 'border-gray-300 text-[#1e3a5f] hover:bg-gray-100'
                    }`}
                  >
                    <Star size={18} className={selectedContact.isFavorite ? 'fill-amber-400' : ''} />
                    <span className="hidden sm:inline">{selectedContact.isFavorite ? 'Favori' : 'Favori'}</span>
                  </button>
                  <button
                    onClick={() => handleDeleteContact(selectedContact.id)}
                    className={`h-12 px-4 flex items-center justify-center gap-2 rounded-xl font-semibold transition-all border ${
                      isDarkMode 
                        ? 'border-red-800 text-red-400 hover:bg-red-900/30' 
                        : 'border-red-200 text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 size={18} />
                  </button>
                  <Button
                    onClick={() => setSelectedContact(null)}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all"
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Ajouter une note */}
      <AnimatePresence>
        {showAddNote && (
          <div className="fixed inset-0 z-[10003] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowAddNote(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-5"
            >
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <StickyNote size={20} className="text-amber-500" />
                Ajouter une note
              </h3>
              
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Écris ta note sur ce contact..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl text-sm border-2 border-gray-200 focus:border-orange-500 focus:ring-0 resize-none"
                autoFocus
              />
              
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddNote(false);
                    setNewNote('');
                  }}
                  className="rounded-xl"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddNote}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                >
                  Ajouter
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
